import React, { useEffect, useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export const DeletePaymentDialog = ({ payment, onOpenChange, onDeleted }) => {
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { if (payment) setConfirmation(""); }, [payment]);

  const nextDue = payment?.invoice_id
    ? Math.min(Number(payment.invoice_total || 0), Number(payment.invoice_balance_due || 0) + Number(payment.amount || 0))
    : null;

  const remove = async () => {
    if (confirmation !== "DELETE") return;
    setDeleting(true);
    try {
      await api.delete(`/mgmt/payments/${payment.id}`);
      toast.success(`Deleted ${fmtMoney(payment.amount)} payment`);
      onDeleted?.(payment);
      onOpenChange(false);
    } catch (error) { toast.error(formatErr(error.response?.data?.detail) || error.message); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open={Boolean(payment)} onOpenChange={onOpenChange}>
      <DialogContent closeTestId="delete-payment-close" className="max-w-lg" data-testid="delete-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700" data-testid="delete-payment-title"><AlertTriangle className="w-5 h-5" />Delete this payment?</DialogTitle>
          <DialogDescription>This permanently removes the selected payment. Check the invoice and amount carefully.</DialogDescription>
        </DialogHeader>
        {payment && (
          <div className="space-y-4">
            <div className="border-2 border-red-200 bg-red-50 rounded-sm p-4 space-y-2 text-sm" data-testid="delete-payment-context">
              <div className="flex justify-between gap-3"><span>Payment</span><strong className="mono-num">{fmtMoney(payment.amount)}</strong></div>
              <div className="flex justify-between gap-3"><span>Colony</span><strong className="text-right">{payment.colony_name || "Colony"}</strong></div>
              <div className="flex justify-between gap-3"><span>Invoice</span><strong>{payment.invoice_number || "Not linked"}</strong></div>
              {payment.invoice_id && <div className="flex justify-between gap-3 border-t border-red-200 pt-2 text-red-800"><span>Invoice will become unpaid</span><strong className="mono-num" data-testid="delete-payment-new-balance">{fmtMoney(nextDue)} due</strong></div>}
            </div>
            <div><label className="text-xs font-semibold" htmlFor="delete-payment-confirmation">Type DELETE to confirm</label><Input id="delete-payment-confirmation" value={confirmation} onChange={(event) => setConfirmation(event.target.value.toUpperCase())} autoComplete="off" data-testid="delete-payment-confirmation" /></div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={deleting} data-testid="delete-payment-cancel">Keep payment</Button>
          <Button type="button" variant="destructive" onClick={remove} disabled={deleting || confirmation !== "DELETE"} data-testid="delete-payment-submit"><Trash2 className="w-4 h-4 mr-2" />{deleting ? "Deleting…" : "Delete payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};